<script>
import Layout from '@/components/layout/app-layout'
import appConfig from '@/app.config'
import PageHeader from '@/components/layout/page-header'
import moment from 'moment'

import api from '@/helpers/api'
import devData from './shared/dev-data/demo.stats.json'

import {
  unionBy,
  reduce,
  sumBy,
  mapValues,
  chain,
  mergeWith,
  defaultTo
} from 'lodash'

export default {
  name: 'stats.reports',
  page: {
    title: 'Data Reporting',
    meta: [{ name: 'description', settings_content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader
  },
  data () {
    return {
      isLoading: false,

      items: [
        {
          text: 'Statistics'
        },
        {
          text: 'Sales',
          active: true
        }
      ],

      report_description: '',
      report_editable: false,

      newTagWatchDialogVisible: false,
      xLabel: moment(),
      endDate: moment(),
      selectedReport: null,
      currentTagWatch: null,
      reports: [],
      tagWatches: [],
      activeTagWatches: [],

      groupedData: {},
      columns: [],
      rowTotals: {},
      rowAverages: {},
      columnTotals: {},
      columnAverages: {},
      grandTotal: 0,
      grandAverage: 0,

      selected_period: {},
      start_date: moment(),
      end_date: moment(),
      periods: {
        isoWeek: {
          title: 'week',
          type: 'week',
          format: '[Week #]w [(]MM/YYYY[)]',
          label: 'weekly',
          options: [...Array.from({ length: 20 }, (v, k) => k + 1)]
        },
        month: {
          title: 'month',
          type: 'month',
          format: 'MMMM YYYY',
          label: 'monthly',
          options: [...Array.from({ length: 12 }, (v, k) => k + 1)]
        },
        year: {
          title: 'year',
          type: 'year',
          format: 'YYYY',
          label: 'yearly',
          options: [...Array.from({ length: 10 }, (v, k) => k + 1)]
        }
      },
      periodOffset: 6
    }
  },
  computed: {
    selectedTagWatchId () {
      return this.currentTagWatch === null ? null : this.currentTagWatch.id
    },
    currentPeriod () {
      var s = moment(this.start_date).format(this.selected_period.format)
      if (this.periodOffset > 1) {
        var e = moment(this.end_date).format(this.selected_period.format)
        return `${s} - ${e}`
      }
      return s
    },
    nextDisabled () {
      return moment(this.end_date).isAfter(moment())
    },
    prevDisabled () {
      return moment(this.start_date).isBefore(moment('01/01/2018'))
    }
  },
  watch: {
    selected_period: 'initPeriods',
    periodOffset: 'initPeriods',
    start_date: 'onRefresh'
  },
  mounted () {
    this.init()
  },
  methods: {
    async init () {
      this.loadReports()
      this.loadTagWatches()

      this.selected_period = this.periods.month
    },

    onRefresh () {
      this.onLoadReport(this.selectedReport)
    },

    async loadTagWatches () {
      //load tag watches
      let { items } = await api.itemTagStatWatch.fetchPage({
        pageSize: -1,
        pageIndex: 0,
        filters: [{}]
      })

      this.tagWatches = items
    },
    async loadReports () {
      let { items } = await api.itemTagStatReport.fetchPage({
        pageSize: -1,
        pageIndex: 0,
        filters: [{}]
      })

      this.reports = items
    },

    async onSaveReport () {
      var report = {}
      if (this.selectedReport) {
        //update
        report = await api.itemTagStatReport.update({
          description: this.report_description,
          id: this.selectedReport.id
        })
        await api.itemTagStatReport.updateWatchLists({
          reportId: this.selectedReport.id,
          watchLists: this.activeTagWatches
        })
        this.report_editable = false
      } else {
        // create
        var data = this.activeTagWatches.map(a => ({
          item_tag_stat_watch_id: a.id
        }))
        report = await api.itemTagStatReport.create({
          description: this.report_description,
          watch_lists: {
            data
          }
        })
        this.report_editable = false
      }

      this.onLoadReport(report)
      this.loadReports()
    },

    async onLoadReport (report) {
      if (!report) return
      let res = await api.itemTagStatReport.fetch({ id: report.id })
      this.selectedReport = { ...res }
      this.activeTagWatches = res.watch_lists.map(a => a.item_tag_stat_watch)
      this.report_description = res.description

      let result = { data: { items: [] } }

      result = await api.fnItemTagStat.fetchAll({
        start_date: moment(this.start_date).format('MM/DD/YYYY'),
        end_date: moment(this.end_date).format('MM/DD/YYYY'),
        tags: this.activeTagWatches.map(a => ({
          label: a.description,
          tags: a.tags
        })),
        frequency: this.selected_period.type
      })

      if (process.env.NODE_ENV !== 'production') {
        result = devData
      }

      // Grouping logic using lodash
      this.groupedData = chain(result)
        .groupBy('start_date')
        .mapValues(startDateGroup =>
          chain(startDateGroup)
            .groupBy('label')
            .mapValues(labelGroup => ({
              sumQuantity: defaultTo(sumBy(labelGroup, 'quantity'), 0),
              sumRevenue: defaultTo(sumBy(labelGroup, 'revenue'), 0)
            }))
            .value()
        )
        .value()

      // Extract Labels (columns)
      this.columns = chain(result).map('label').uniq().value()

      // Calculate row totals
      this.rowTotals = mapValues(this.groupedData, startDateGroup =>
        reduce(
          startDateGroup,
          (acc, labelGroup) => ({
            sumQuantity: acc.sumQuantity + labelGroup.sumQuantity,
            sumRevenue: acc.sumRevenue + labelGroup.sumRevenue
          }),
          { sumQuantity: 0, sumRevenue: 0 }
        )
      )

      // Calculate column totals
      this.columnTotals = chain(this.groupedData)
        .map(startDateGroup =>
          mapValues(startDateGroup, labelGroup => labelGroup.sumQuantity)
        )
        .reduce(
          (acc, obj) => mergeWith(acc, obj, (a, b) => (a || 0) + (b || 0)),
          {}
        )
        .value()

      // Calculate column averages
      this.columnAverages = mapValues(
        this.columnTotals,
        (sumQuantity, xLabel) => Math.floor(sumQuantity / this.columns.length)
      )

      // Calculate row averages
      this.rowAverages = mapValues(this.rowTotals, (rowTotal, label) =>
        Math.floor(rowTotal.sumQuantity / this.columns.length)
      )

      // Calculate grand total
      this.grandTotal = chain(this.columnTotals).values().sum().value()

      // Calculate grand average
      this.grandAverage = Math.floor(this.grandTotal / this.columns.length)
    },

    onReportClosed () {
      this.resetReport()
    },

    async onTagWatchSelected (tw) {
      var len = this.activeTagWatches.length
      this.activeTagWatches = unionBy(this.activeTagWatches, [tw], 'id')
      if (!this.report_editable)
        this.report_editable = len !== this.activeTagWatches.length
    },

    async onTagWatchRemoved (tw) {
      this.activeTagWatches = [
        ...this.activeTagWatches.filter(a => a.id !== tw.id)
      ]

      if (this.activeTagWatches.length == 0 && !this.selectedReport) {
        this.report_editable = false
      }
    },

    onNewWatchList () {
      //open new dialog
      this.currentTagWatch = null
      this.newTagWatchDialogVisible = true
    },

    resetReport () {
      this.selectedReport = null
      this.activeTagWatches = []
      this.report_editable = false
      this.report_description = ''
    },

    onEditTagWatch (tw) {
      this.currentTagWatch = tw
      this.newTagWatchDialogVisible = true
      this.resetReport()
    },

    async onRemoveTagWatch (tw) {
      if (!tw) return
      await api.itemTagStatWatch.delete({ id: tw.id })

      this.resetReport()
      this.init()
    },

    async onRemoveReport () {
      if (this.selectedReport) {
        await api.itemTagStatReport.delete({ id: this.selectedReport.id })
        this.init()
      }
      this.resetReport()
    },

    async onNewTagWatchCreatedHandler (tw) {
      // create new tag watch
      if (this.currentTagWatch) {
        await api.itemTagStatWatch.update({
          ...tw,
          id: this.selectedTagWatchId
        })
        this.currentTagWatch = { ...tw }
      } else await api.itemTagStatWatch.create({ ...tw })

      this.init()
    },

    /* Filtering Periods */
    initPeriods () {
      this.start_date = moment()
        .subtract(this.periodOffset - 1, this.selected_period.type)
        .startOf(this.selected_period.type)

      this.end_date = moment(this.start_date)
        .add(this.periodOffset - 1, this.selected_period.type)
        .endOf(this.selected_period.type)
    },

    nextPeriod () {
      if (this.nextDisabled) return
      this.start_date = moment(this.start_date)
        .add(1, this.selected_period.type)
        .startOf(this.selected_period.type)
      this.end_date = moment(this.start_date)
        .add(this.periodOffset - 1, this.selected_period.type)
        .endOf(this.selected_period.type)
    },
    prevPeriod () {
      if (this.prevDisabled) return
      this.start_date = moment(this.start_date)
        .subtract(1, this.selected_period.type)
        .startOf(this.selected_period.type)
      this.end_date = moment(this.start_date)
        .add(this.periodOffset - 1, this.selected_period.type)
        .endOf(this.selected_period.type)
    }
  }
}
</script>
<template>
  <Layout>
    <PageHeader :items="items">
      <div slot="title" class="d-flex align-items-center">
        <h4 class="mb-0">{{ $t('Reports') }}</h4>
      </div>
    </PageHeader>

    <b-row>
      <b-col :cols="3">
        <!-- Reports Card -->
        <b-card no-body class="shadow-sm border border-light">
          <div class="vstack justify-content-start p-2 m-0">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-muted">
                <i class="mdi mdi-file-chart-outline mr-2"></i>Reports
              </h6>
              <b-btn
                variant="icon"
                @click="loadReports"
                class="text-muted px-1"
              >
                <i class="mdi mdi-sync"></i>
              </b-btn>
            </div>
            <div style="max-height: 200px; overflow-y: auto">
              <div
                class="d-flex justify-content-between align-items-center"
                v-for="report in reports"
                :key="report.id"
              >
                <b-btn
                  variant="black"
                  class="px-1"
                  @click="onLoadReport(report)"
                >
                  {{ report.description }}
                </b-btn>
              </div>
            </div>
          </div>
        </b-card>
        <!-- Tag Watch List Card -->
        <b-card no-body class="shadow-sm border border-light">
          <div class="vstack justify-content-start p-2 m-0">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-muted">
                <i class="mdi mdi-tag-outline mr-2"></i>Tag Lists
              </h6>
              <b-btn
                variant="icon"
                @click="onNewWatchList"
                class="text-muted px-1"
              >
                <i class="mdi mdi-plus-box-outline"></i>
              </b-btn>
            </div>
            <div style="max-height: 300px; overflow-y: auto">
              <div
                class="d-flex justify-content-between align-items-center"
                v-for="tw in tagWatches"
                :key="tw.id"
              >
                <b-btn
                  variant="black"
                  class="px-1 text-truncate"
                  :title="tw.description"
                  @click="onTagWatchSelected(tw)"
                >
                  {{ tw.description }}
                </b-btn>

                <div class="d-flex justify-content-between align-items-center">
                  <b-btn
                    variant="icon"
                    class="px-1"
                    @click="onEditTagWatch(tw)"
                  >
                    <i class="mdi mdi-pencil"></i>
                  </b-btn>
                  <b-btn
                    variant="icon"
                    class="px-1"
                    @click="onRemoveTagWatch(tw)"
                  >
                    <i class="mdi mdi-trash-can-outline text-danger"></i>
                  </b-btn>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col :cols="9">
        <b-card class="p-3 shadow-sm" no-body>
          <div class="d-flex justify-content-between align-items-end">
            <div class="d-flex flex-column">
              <h5>
                {{ currentPeriod }}
              </h5>
              <p class="text-muted m-0">
                {{ start_date | moment('DD/MM/YY') }} -
                {{ end_date | moment('DD/MM/YY') }}
              </p>
            </div>

            <b-btn-group class="m-0">
              <b-btn
                size="sm"
                variant="link"
                @click="prevPeriod"
                :disabled="prevDisabled"
              >
                <i class="bx bx-chevrons-left" />
              </b-btn>
              <b-btn
                size="sm"
                :variant="selected_period !== period ? 'link' : 'primary'"
                v-for="period in periods"
                :key="period.title"
                @click="selected_period = period"
                >{{ period.title }}</b-btn
              >
              <b-btn
                size="sm"
                variant="link"
                @click="nextPeriod"
                :disabled="nextDisabled"
              >
                <i class="bx bx-chevrons-right" />
              </b-btn>

              <b-form-select
                v-model="periodOffset"
                :options="selected_period.options"
                size="sm"
              ></b-form-select>
            </b-btn-group>
          </div>
        </b-card>

        <b-card
          no-body
          class="shadow-sm border border-light"
          v-if="selectedReport || activeTagWatches.length > 0"
        >
          <div
            class="p-4 px-4 d-flex justify-content-between align-items-center"
            v-if="selectedReport"
          >
            <div
              class="d-flex justify-content-end align-items-center"
              v-if="!report_editable"
            >
              <h6 class="m-0 mr-4" v-if="!report_editable">
                {{ selectedReport.description }}
              </h6>
              <b-btn
                variant="icon"
                class="px-1"
                @click="report_editable = true"
              >
                <i class="mdi mdi-pencil"></i>
              </b-btn>
            </div>

            <b-form-input
              v-else
              size="sm"
              id="report_description"
              v-model="report_description"
              type="text"
              required
              style="width: 300px"
              autocomplete="report_description"
              placeholder="Description, or short name"
            ></b-form-input>

            <div class="d-flex justify-content-end align-items-center">
              <b-btn variant="icon" class="px-1" @click="onSaveReport()">
                <i class="mdi mdi-content-save-outline"></i>
              </b-btn>

              <b-btn variant="icon" class="px-1" @click="onRemoveReport()">
                <i class="mdi mdi-trash-can-outline text-danger"></i>
              </b-btn>
              <b-btn variant="icon" @click="onReportClosed()" class="px-1">
                <i class="mdi mdi-close-thick"></i>
              </b-btn>
            </div>
          </div>
          <div
            v-if="!selectedReport && activeTagWatches.length > 0"
            class="p-3 d-flex justify-content-between align-items-end"
          >
            <b-form-input
              size="sm"
              v-model="report_description"
              type="text"
              required
              style="width: 300px"
              autocomplete="report_description"
              placeholder="Description, or short name"
            ></b-form-input>

            <div class="d-flex justify-content-end align-items-end">
              <b-btn variant="icon" class="px-1" @click="onSaveReport()">
                <i class="bx bxs-save"></i>
              </b-btn>

              <b-btn variant="icon" class="px-1" @click="onRemoveReport()">
                <i class="mdi mdi-trash-can-outline text-danger"></i>
              </b-btn>
            </div>
          </div>
          <div v-for="tw in activeTagWatches" :key="tw.id">
            <b-card-header header-bg-variant="white" class="py-1 px-4">
              <b-row align-v="center">
                <b-col :cols="6" align-self="center">
                  <h6 class="text-muted m-0">
                    {{ tw.description
                    }}<b-btn
                      variant="icon"
                      class="px-2 py-0"
                      :title="$t('remove from report')"
                      @click="onTagWatchRemoved(tw)"
                      v-if="report_editable"
                    >
                      <i class="mdi mdi-trash-can-outline text-danger"></i>
                    </b-btn>
                  </h6>
                </b-col>
                <b-col>
                  <div class="d-flex justify-content-end align-items-center">
                    <b-badge
                      variant="dark"
                      class="mx-1"
                      v-for="tag in tw.tags"
                      :key="`${tw.id}_${tag}`"
                      >{{ tag }}</b-badge
                    >
                  </div>
                </b-col>
              </b-row>
            </b-card-header>
          </div>
          <h6 class="text-muted px-4 mt-4">Data</h6>
          <div class="p-4">
            <div class="table-container">
              <table class="mx-0">
                <thead>
                  <tr>
                    <th class="fixed-column">Label</th>
                    <th
                      class="text-center px-3"
                      v-for="xLabel in columns"
                      :key="`${xLabel}_header`"
                    >
                      {{ xLabel }}
                    </th>
                    <th class="text-center fixed-column-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(group, label) in groupedData" :key="label">
                    <td class="fixed-column">
                      {{ label | moment(selected_period.format) }}
                    </td>
                    <td
                      class="text-center"
                      v-for="xLabel in columns"
                      :key="`${xLabel}_${label}`"
                    >
                      {{ group[xLabel] ? group[xLabel].sumQuantity : '' }}
                    </td>
                    <td class="text-center fixed-column-right">
                      {{ rowTotals[label] ? rowTotals[label].sumQuantity : '' }}
                    </td>
                  </tr>
                  <tr>
                    <td class="fixed-column">Total</td>
                    <td
                      class="text-center fixed-column"
                      v-for="xLabel in columns"
                      :key="`${xLabel}_tot`"
                    >
                      {{ columnTotals[xLabel] ? columnTotals[xLabel] : '' }}
                    </td>
                    <td class="text-center fixed-column-right">
                      {{ grandTotal }}
                    </td>
                  </tr>
                  <tr>
                    <td class="fixed-column">Avg.</td>
                    <td
                      class="text-center fixed-column"
                      v-for="xLabel in columns"
                      :key="`${xLabel}_avg`"
                    >
                      {{ columnAverages[xLabel] ? columnAverages[xLabel] : '' }}
                    </td>

                    <td class="text-center fixed-column-right">
                      {{ grandAverage }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </b-card>
        <b-card v-if="!selectedReport && activeTagWatches.length == 0">
          <b-row class="d-flex p-4 align-items-center justify-content-center">
            <h6 class="text-muted">Select or create a report</h6>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <app-tag-watch-form-dialog
      :reportId="selectedTagWatchId"
      :visible.sync="newTagWatchDialogVisible"
      v-on:created="onNewTagWatchCreatedHandler"
    />
  </Layout>
</template>

<style>
.dropdown-menu {
  min-width: 1rem !important;
}

.table-container {
  overflow-x: auto;
  overflow-y: auto;
}

.fixed-column,
.fixed-column-right {
  position: sticky;
  background-color: #f5f5f5;
  z-index: 1;
}

.fixed-column:first-child {
  min-width: 200px;
  left: 0;
}

.fixed-column-right:nth-last-child(1) {
  right: 0;
}
.fixed-column-right:nth-last-child(2) {
  right: 100px;
}

table {
  border-collapse: collapse;
}

th,
td {
  padding: 5px;
  text-align: left;
  min-width: 100px;
}

th {
  background-color: #f2f2f2;
}

td {
  background-color: #fff;
}
</style>
